import React, { useEffect, useState, ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Address, Quote, Product } from '../../types';
import {
  fetchQuoteById,
  selectSelectedQuote,
  selectLoading,
  selectError,
} from '../../redux/slices/quoteSlice';
import * as quoteService from '../../services/quote-service';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import CreditAnalysisConfirmDialog from '../../components/CreditAnalysisConfirmDialog/CreditAnalysisConfirmDialog';
import Layout from '../../components/Layout/Layout';
import BackButton from '../../components/BackButton/BackButton';
import DataTable, { StatusColumn } from '../../components/DataTable/DataTable';
import styles from './QuoteDetailsPage.module.css';

// Material UI imports
import {
  Button,
  Typography,
  Grid,
  Paper,
  Chip,
  Box,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  GetApp as DownloadIcon,
  Share as ShareIcon,
  Check as CheckCircleIcon,
  Home,
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Contacts as ContactsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { getProductsForQuote } from '../../services/product-service';

// Types for quote items in the API response
interface QuoteItemWithoutProduct {
  product_id: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  discount_amount: number;
  id: string;
  quote_id: string;
  product?: {
    id: string;
    sku: string;
    name: string;
    description?: string;
    price?: number;
    specifications?: string;
    images?: Array<{
      image_url: string;
      alt_text: string;
      is_primary: boolean;
    }>;
  };
}

// Extended Quote type to include quote_items from API response
interface QuoteWithItems extends Omit<Quote, 'status'> {
  status: 'Draft' | 'Submitted' | 'Aprovado' | 'Rejected' | 'Analise de Credito';
  quote_items?: QuoteItemWithoutProduct[];
  created_at?: string;
  shipping_address?: {
    street: string;
    city: string;
    state: string;
    zip_code: string;
    country: string;
    is_default: boolean;
    id: string;
    customer_id: string;
  };
}

interface ProductInfo {
  id: string;
  sku: string;
  name: string;
}

const QuoteDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const quote = useAppSelector(selectSelectedQuote) as QuoteWithItems; // Cast to extended type
  const loading = useAppSelector(selectLoading);
  const error = useAppSelector(selectError);
  const { setBreadcrumbs } = useBreadcrumb();
  const [quoteItems, setQuoteItems] = useState<QuoteItemWithoutProduct[]>([]);
  const [products, setProducts] = useState<Record<string, ProductInfo>>({});
  const { enqueueSnackbar } = useSnackbar();
  const [openAddProductDialog, setOpenAddProductDialog] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});
  const [editingItem, setEditingItem] = useState<QuoteItemWithoutProduct | null>(null);
  const [editQuantity, setEditQuantity] = useState<number>(0);
  const [addingProductId, setAddingProductId] = useState<string | null>(null);
  const [savingItemId, setSavingItemId] = useState<string | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [deletingQuote, setDeletingQuote] = useState(false);
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchQuoteById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    // Handle the quote_items from the response
    if (quote && quote.quote_items) {
      setQuoteItems(quote.quote_items);

      // Fetch product details for each item if needed
      const productIds = quote.quote_items.map((item: QuoteItemWithoutProduct) => item.product_id);

      // If the quote_items already contain product information, use that directly
      if (quote.quote_items[0] && 'product' in quote.quote_items[0]) {
        const productMap: Record<string, ProductInfo> = {};
        quote.quote_items.forEach((item: any) => {
          if (item.product) {
            productMap[item.product_id] = {
              id: item.product.id,
              sku: item.product.sku,
              name: item.product.name,
            };
          }
        });
        setProducts(productMap);
      } else if (productIds.length > 0) {
        // Fall back to fetching product details if not included
        fetchProductDetails(productIds);
      }
    }
  }, [quote]);

  // Function to fetch product details
  const fetchProductDetails = async (productIds: string[]) => {
    try {
      // This would be replaced with your actual API call to get product details
      // For example:
      // const response = await productService.getProductsByIds(productIds);
      // setProducts(response.data.reduce((acc, product) => {
      //   acc[product.id] = product;
      //   return acc;
      // }, {}));

      // For now, we'll handle this by setting mock product data
      // This should be replaced with actual API call
      const mockProducts: Record<string, ProductInfo> = {};
      productIds.forEach(id => {
        mockProducts[id] = {
          id,
          sku: `SKU-${id.substring(0, 6)}`,
          name: `Product ${id.substring(0, 6)}`,
        };
      });
      setProducts(mockProducts);
    } catch (error) {
      console.error('Error fetching product details:', error);
    }
  };

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
      { label: 'Cotações', path: '/quotes' },
      { label: quote ? `Cotação - ${quote.code || id}` : `Cotação #${id}` },
    ]);

    return () => {
      setBreadcrumbs([{ label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> }]);
    };
  }, [quote, id, setBreadcrumbs]);

  const getStatusChip = (status: string) => {
    /* eslint-disable */
    const statusMap: Record<
      string,
      {
        color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
        label: string;
      }
    > = {
      Draft: { color: 'default', label: 'Rascunho' },
      Submitted: { color: 'info', label: 'Enviado' },
      Pending: { color: 'warning', label: 'Em Análise' },
      'Analise de Credito': { color: 'warning', label: 'Análise de Crédito' },
      Approved: { color: 'success', label: 'Aprovado' },
      Rejected: { color: 'error', label: 'Rejeitado' },
      Converted: { color: 'primary', label: 'Convertido' },
    };

    const statusInfo = statusMap[status] || { color: 'default', label: status };
    return (
      <Chip label={statusInfo.label} color={statusInfo.color} size="small" variant="outlined" />
    );
  };
  /* eslint-enable */
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const [converting, setConverting] = useState(false);

  // Função para abrir o dialog de confirmação de análise de crédito
  const handleConvertToOrder = async () => {
    setCreditDialogOpen(true);
  };

  // Função para processar a conversão após confirmação
  const handleCreditAnalysisConfirm = async (verifyCredit: boolean) => {
    if (!id) return;

    try {
      setConverting(true);
      setCreditDialogOpen(false);

      const response = await quoteService.convertQuoteToOrder(id, verifyCredit);

      if (response.success) {
        // Check if order_id is directly in data or in a nested property
        const orderId = response.data.order_id || (response.data as any).id;

        if (orderId) {
          // Display success message
          enqueueSnackbar('Orçamento convertido em pedido com sucesso!', {
            variant: 'success',
            autoHideDuration: 3000,
          });

          // Navigate to the order details page
          navigate(`/orders/${orderId}`);
        } else {
          enqueueSnackbar('Pedido criado, mas não foi possível obter o ID do pedido', {
            variant: 'warning',
            autoHideDuration: 5000,
          });
        }
      } else {
        // Verificar se a mensagem está relacionada à análise de crédito
        if (
          response.message &&
          (response.message.includes('análise de crédito') ||
            response.message.includes('credit') ||
            response.message.includes('Credix'))
        ) {
          // Atualizar a cotação no backend para garantir que o status seja atualizado
          try {
            await quoteService.updateQuote(id, { status: 'Analise de Credito' });
            // Recarregar a cotação para atualizar a interface
            if (id) {
              dispatch(fetchQuoteById(id));
            }
          } catch (updateError) {
            console.error('Erro ao atualizar status da cotação:', updateError);
          }

          // Mostrar mensagem amigável
          enqueueSnackbar(
            'Cotação enviada para análise de crédito. Um administrador irá revisar seu pedido.',
            {
              variant: 'info',
              autoHideDuration: 6000,
            }
          );
        } else {
          // Não exibir mensagem de erro para outros casos
        }
        // Registrar o erro no console para debug
        console.error('Failed to convert quote to order:', response.message);
      }
    } catch (error) {
      // Atualizar a cotação no backend para garantir que o status seja atualizado
      try {
        await quoteService.updateQuote(id, { status: 'Analise de Credito' });
        // Recarregar a cotação para atualizar a interface
        if (id) {
          dispatch(fetchQuoteById(id));
        }
      } catch (updateError) {
        console.error('Erro ao atualizar status da cotação:', updateError);
      }

      // Mostrar mensagem amigável sem detalhes do erro
      enqueueSnackbar('Cotação enviada para análise. Um administrador irá revisar seu pedido.', {
        variant: 'info',
        autoHideDuration: 6000,
      });

      // Registrar o erro no console para debug
      console.error('Error converting quote to order:', error);
    } finally {
      setConverting(false);
    }
  };

  // Função para fechar o dialog de confirmação
  const handleCreditDialogClose = () => {
    setCreditDialogOpen(false);
  };

  const handleGeneratePDF = async () => {
    try {
      enqueueSnackbar('Gerando PDF...', {
        variant: 'info',
        autoHideDuration: 2000,
      });

      const jsPDF = (await import('jspdf')).default;
      const pdf = new jsPDF('p', 'pt', 'a4');

      // Configurações
      const margin = 40;
      const pageWidth = pdf.internal.pageSize.width - 2 * margin;
      let yPosition = margin;
      const lineHeight = 20;
      const pageHeight = pdf.internal.pageSize.height - margin;

      // Função para adicionar texto com quebra de linha
      const addTextWithWrap = (text: string, x: number, y: number, maxWidth: number) => {
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return lines.length * lineHeight;
      };

      // Cabeçalho
      pdf.setFontSize(18);
      pdf.setTextColor(33, 33, 33);
      pdf.text(`Cotação ${quote.code || id}`, margin, yPosition);
      yPosition += lineHeight * 2;

      // Informações básicas - título
      pdf.setFontSize(14);
      pdf.text('Informações Básicas', margin, yPosition);
      yPosition += lineHeight * 1.5;

      // Configurações para o texto das informações
      pdf.setFontSize(10);
      const labelWidth = 120;
      const valueX = margin + labelWidth + 10;
      const valueWidth = pageWidth - labelWidth - 10;

      // Cliente
      pdf.text('Cliente:', margin, yPosition);
      yPosition += addTextWithWrap(
        quote.customer?.company_name || 'Não informado',
        valueX,
        yPosition,
        valueWidth
      );

      // Data da Criação
      yPosition += lineHeight * 0.5;
      pdf.text('Data da Criação:', margin, yPosition);
      yPosition += addTextWithWrap(
        quote.created_at ? new Date(quote.created_at).toLocaleDateString('pt-BR') : 'N/A',
        valueX,
        yPosition,
        valueWidth
      );

      // Status
      yPosition += lineHeight * 0.5;
      pdf.text('Status:', margin, yPosition);
      yPosition += addTextWithWrap(translateStatus(quote.status), valueX, yPosition, valueWidth);

      // Endereço de Entrega
      yPosition += lineHeight * 0.5;
      pdf.text('Endereço de Entrega:', margin, yPosition);
      const addressText = quote.shipping_address
        ? `${quote.shipping_address.street}, ${quote.shipping_address.city}/${quote.shipping_address.state}, ${quote.shipping_address.zip_code}, ${quote.shipping_address.country}`
        : 'Não informado';
      yPosition += addTextWithWrap(addressText, valueX, yPosition, valueWidth);

      // Validade da Cotação
      yPosition += lineHeight * 0.5;
      pdf.text('Validade da Cotação:', margin, yPosition);
      yPosition += addTextWithWrap(
        quote.expiration_date
          ? new Date(quote.expiration_date).toLocaleDateString('pt-BR')
          : 'Não informado',
        valueX,
        yPosition,
        valueWidth
      );

      // Criado por
      yPosition += lineHeight * 0.5;
      pdf.text('Criado por:', margin, yPosition);
      yPosition += addTextWithWrap(
        quote.reseller?.name || 'Não informado',
        valueX,
        yPosition,
        valueWidth
      );

      // Adicionar espaço antes da próxima seção
      yPosition += lineHeight;

      // Verificar se precisa de nova página
      if (yPosition > pageHeight - 200) {
        pdf.addPage();
        yPosition = margin;
      }

      // Adicionar tabela de produtos
      pdf.setFontSize(14);
      pdf.text('Produtos', margin, yPosition);
      yPosition += lineHeight;

      // Cabeçalho da tabela
      pdf.setFontSize(10);
      pdf.setTextColor(255, 255, 255);
      pdf.setFillColor(128, 195, 67);
      pdf.rect(margin, yPosition, 500, lineHeight, 'F');

      pdf.text('Produto', margin + 10, yPosition + 15);
      pdf.text('Quantidade', margin + 200, yPosition + 15);
      pdf.text('Preço Unitário', margin + 300, yPosition + 15);
      pdf.text('Subtotal', margin + 400, yPosition + 15);
      yPosition += lineHeight;

      // Linhas da tabela
      pdf.setTextColor(0, 0, 0);
      itemsWithSubtotal.forEach((item, index) => {
        if (yPosition > pageHeight - 100) {
          pdf.addPage();
          yPosition = margin;
        }

        const bgColor = index % 2 === 0 ? [245, 245, 245] : [255, 255, 255];
        pdf.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
        pdf.rect(margin, yPosition, 500, lineHeight, 'F');

        const productName =
          item.product?.name || products[item.product_id]?.name || 'Produto desconhecido';
        pdf.text(productName.substring(0, 30), margin + 10, yPosition + 15);
        pdf.text(item.quantity.toString(), margin + 200, yPosition + 15);
        pdf.text(formatCurrency(item.unit_price), margin + 300, yPosition + 15);
        pdf.text(formatCurrency(item.subtotal), margin + 400, yPosition + 15);
        yPosition += lineHeight;
      });

      // Total
      yPosition += lineHeight;
      pdf.setFontSize(12);
      pdf.text(`Total: ${formatCurrency(total)}`, margin + 350, yPosition);
      yPosition += lineHeight * 2;

      // Condições comerciais
      pdf.setFontSize(14);
      pdf.text('Condições Comerciais', margin, yPosition);
      yPosition += lineHeight;

      pdf.setFontSize(10);
      pdf.text(
        `Método de pagamento: ${quote.payment_method ? translatePaymentMethod(quote.payment_method) : 'Não informado'}`,
        margin,
        yPosition
      );
      yPosition += lineHeight;
      pdf.text(
        `Prazo de pagamento: ${quote.payment_term + ' dias' || 'Não informado'}`,
        margin,
        yPosition
      );

      // Salvar o PDF
      const fileName = `Cotacao_${quote.code || id}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      enqueueSnackbar('PDF gerado com sucesso!', {
        variant: 'success',
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      enqueueSnackbar('Erro ao gerar PDF. Tente novamente.', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  };

  const handleShareLink = () => {
    // TODO: Implement share functionality
    console.log('Sharing link for quote:', id);
  };

  const handleOpenContactsModal = () => {
    setOpenContactsModal(true);
  };

  const handleCloseContactsModal = () => {
    setOpenContactsModal(false);
  };

  const translatePaymentMethod = (method: string): string => {
    switch (method) {
      case 'Partial Credit':
        return 'Crédito Parcial';
      case 'Bank Slip':
        return 'Boleto Bancário';
      default:
        return '';
    }
  };

  const translateStatus = (status: string): string => {
    switch (status) {
      case 'Draft':
        return 'Rascunho';
      case 'Submitted':
        return 'Enviado';
      case 'Pending':
        return 'Em Análise';
      case 'Approved':
        return 'Aprovado';
      case 'Rejected':
        return 'Rejeitado';
      case 'Converted':
        return 'Convertido';
      default:
        return '';
    }
  };

  const columns: StatusColumn[] = [
    {
      id: 'product_id',
      label: 'Produto',
      minWidth: 200,
      format: (value: unknown, row: any): ReactNode => {
        if (row.product) {
          return row.product.name;
        }
        const productId = value as string;
        return products[productId]?.name || 'Carregando...';
      },
    },
    {
      id: 'sku',
      label: 'SKU',
      minWidth: 120,
      format: (_: unknown, row: any): ReactNode => {
        if (row.product) {
          return row.product.sku;
        }
        const productId = row.product_id;
        return products[productId]?.sku || 'Carregando...';
      },
    },
    {
      id: 'quantity',
      label: 'Quantidade',
      minWidth: 100,
      align: 'right',
      format: (value: unknown, row: any): ReactNode => {
        if (editingItem?.id === row.id) {
          return (
            <TextField
              type="number"
              size="small"
              value={editQuantity}
              onChange={e => setEditQuantity(Number(e.target.value))}
              inputProps={{ min: 1 }}
              sx={{ width: 80 }}
            />
          );
        }
        return String(value);
      },
    },
    {
      id: 'unit_price',
      label: 'Preço Unitário (R$)',
      minWidth: 150,
      align: 'right',
      format: (value: unknown): ReactNode => formatCurrency(value as number),
    },
    {
      id: 'discount_percent',
      label: 'Desconto (%)',
      minWidth: 100,
      align: 'right',
      format: (value: unknown): ReactNode => {
        const discountPercent = value as number;
        return discountPercent > 0 ? `${discountPercent}%` : '-';
      },
    },
    {
      id: 'subtotal',
      label: 'Subtotal (R$)',
      minWidth: 150,
      align: 'right',
      format: (value: unknown): ReactNode => formatCurrency(value as number),
    },
    {
      id: 'actions',
      label: 'Ações',
      minWidth: 120,
      align: 'center',
      format: (_: unknown, row: any): ReactNode => {
        if (editingItem?.id === row.id) {
          return (
            <Box display="flex" gap={1} justifyContent="center">
              <IconButton
                size="small"
                onClick={handleSaveEdit}
                sx={{ color: '#80c343' }}
                disabled={savingItemId === row.id}
              >
                {savingItemId === row.id ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon fontSize="small" />
                )}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setEditingItem(null)}
                color="error"
                disabled={savingItemId === row.id}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }
        return (
          <Box display="flex" gap={1} justifyContent="center">
            <IconButton size="small" onClick={() => handleEditItem(row)} sx={{ color: '#80c343' }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteItem(row.id)}
              color="error"
              disabled={deletingItemId === row.id}
            >
              {deletingItemId === row.id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        );
      },
    },
  ];

  // Calculate subtotal for each item and total
  const itemsWithSubtotal = quoteItems.map((item: QuoteItemWithoutProduct) => {
    // Calculate subtotal with discount
    const totalBeforeDiscount = item.unit_price * item.quantity;
    let discountAmount = 0;

    if (item.discount_percent > 0) {
      discountAmount = totalBeforeDiscount * (item.discount_percent / 100);
    } else if (item.discount_amount > 0) {
      discountAmount = item.discount_amount;
    }

    const subtotal = totalBeforeDiscount - discountAmount;

    return {
      ...item,
      subtotal,
    };
  });

  const total = itemsWithSubtotal.reduce(
    (sum: number, item: QuoteItemWithoutProduct & { subtotal: number }) => sum + item.subtotal,
    0
  );

  const handleOpenAddProductDialog = async () => {
    setOpenAddProductDialog(true);
    await fetchProducts();
  };

  const handleCloseAddProductDialog = () => {
    setOpenAddProductDialog(false);
    setSearchTerm('');
    setSelectedQuantities({});
  };

  const fetchProducts = async (query = '') => {
    if (!quote?.customer_id) {
      enqueueSnackbar('Cliente da cotação não encontrado', { variant: 'error' });
      setAvailableProducts([]);
      return;
    }

    setLoadingProducts(true);
    try {
      const result = await getProductsForQuote(quote.customer_id, 1, 20, query);
      if (result) {
        setAvailableProducts(result.items);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      enqueueSnackbar('Erro ao carregar produtos', { variant: 'error' });
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSearch = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (value.trim()) {
      await fetchProducts(value);
    } else {
      await fetchProducts();
    }
  };

  const handleQuantityChange = (productId: string, value: string) => {
    const quantity = parseInt(value) || 0;
    setSelectedQuantities(prev => ({
      ...prev,
      [productId]: quantity,
    }));
  };

  const handleAddProduct = async (product: Product) => {
    if (!id) {
      enqueueSnackbar('ID da cotação não encontrado', { variant: 'error' });
      return;
    }

    try {
      setAddingProductId(product.id);
      const response = await quoteService.addQuoteItem(id, {
        product_id: product.id,
        quantity: 1,
        unit_price: product.price,
      });

      if (response.success) {
        enqueueSnackbar('Produto adicionado com sucesso', { variant: 'success' });

        // Update the quote items directly instead of reloading the entire quote
        const newItem: QuoteItemWithoutProduct = {
          id: response.data.id,
          quote_id: id,
          product_id: product.id,
          quantity: 1,
          unit_price: product.price,
          discount_percent: 0,
          discount_amount: 0,
          product: {
            id: product.id,
            sku: product.sku,
            name: product.name,
            price: product.price,
            images: product.images?.map(img => ({
              image_url: img.image_url,
              alt_text: img.alt_text || product.name,
              is_primary: img.is_primary,
            })),
          },
        };

        setQuoteItems(prevItems => [...prevItems, newItem]);
      } else {
        throw new Error('Falha ao adicionar produto');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      enqueueSnackbar('Erro ao adicionar produto', { variant: 'error' });
    } finally {
      setAddingProductId(null);
    }
  };

  const handleEditItem = (item: QuoteItemWithoutProduct) => {
    setEditingItem(item);
    setEditQuantity(item.quantity);
  };

  const handleSaveEdit = async () => {
    if (!editingItem || !id) return;

    try {
      setSavingItemId(editingItem.id);
      const response = await quoteService.updateQuoteItem(id, editingItem.id, {
        quantity: editQuantity,
      });

      if (response.success) {
        enqueueSnackbar('Item atualizado com sucesso.', { variant: 'success' });

        // Update the quote items directly instead of reloading the entire quote
        setQuoteItems(prevItems =>
          prevItems.map(item =>
            item.id === editingItem.id ? { ...item, quantity: editQuantity } : item
          )
        );

        setEditingItem(null);
      } else {
        throw new Error('Falha ao atualizar o item.');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      enqueueSnackbar('Falha ao atualizar o item.', { variant: 'error' });
    } finally {
      setSavingItemId(null);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!id) return;

    try {
      setDeletingItemId(itemId);
      const response = await quoteService.removeQuoteItem(id, itemId);

      if (response.success) {
        enqueueSnackbar('Item removido com sucesso', { variant: 'success' });

        // Update the quote items directly instead of reloading the entire quote
        setQuoteItems(prevItems => prevItems.filter(item => item.id !== itemId));
      } else {
        throw new Error('Falha ao remover item');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      enqueueSnackbar('Erro ao remover item', { variant: 'error' });
    } finally {
      setDeletingItemId(null);
    }
  };

  const handleDeleteQuote = async () => {
    if (!id) return;

    try {
      setDeletingQuote(true);
      const response = await quoteService.deleteQuote(id);

      if (response.success) {
        enqueueSnackbar('Cotação excluída com sucesso', { variant: 'success' });
        navigate('/quotes');
      } else {
        throw new Error('Falha ao excluir cotação');
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      enqueueSnackbar('Houve um erro ao excluir a cotação', { variant: 'error' });
    } finally {
      setDeletingQuote(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="calc(100vh - 200px)"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="calc(100vh - 200px)"
      >
        <Typography variant="h6" color="error">
          Erro ao carregar cotação: {error}
        </Typography>
      </Box>
    );
  }

  if (!quote) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="calc(100vh - 200px)"
      >
        <Typography variant="h6">Cotação não encontrada</Typography>
      </Box>
    );
  }

  return (
    <>
      <BackButton onClick={() => navigate('/quotes')} />
      <div id="quote-details-content">
        <Box mb={4}>
          <Typography variant="h5" gutterBottom>
            Sua cotação, pronta para ser transformada em pedido!
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Aqui está o resumo completo da sua cotação. Revise os produtos, confira as condições
            comerciais e finalize sua compra com um clique. Se precisar ajustar algo, edite antes de
            transformar em pedido.
          </Typography>
        </Box>

        <Paper className={styles.section} elevation={1}>
          <Box borderBottom="1px solid #d9d9d9" pb={2} mb={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">
                Detalhe da cotação - {quote.customer?.company_name || 'Cliente'}
              </Typography>
              <Button
                color="primary"
                startIcon={<ContactsIcon />}
                onClick={handleOpenContactsModal}
                size="small"
              >
                Ver Contatos
              </Button>
            </Box>
          </Box>

          <Grid container spacing={4} mb={4}>
            <Grid item xs={12} md={6}>
              <Box display="flex" mb={1}>
                <Typography variant="body2" sx={{ width: '200px' }}>
                  Número da Cotação:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {quote.code || id}
                </Typography>
              </Box>
              <Box display="flex" mb={1}>
                <Typography variant="body2" sx={{ width: '200px' }}>
                  Data da Criação:
                </Typography>
                <Typography variant="body2">
                  {quote.created_at
                    ? new Date(quote.created_at).toLocaleDateString('pt-BR')
                    : 'N/A'}
                </Typography>
              </Box>
              <Box display="flex">
                <Typography variant="body2" sx={{ width: '200px' }}>
                  Status:
                </Typography>
                {getStatusChip(quote.status)}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" mb={1}>
                <Typography variant="body2" sx={{ width: '200px' }}>
                  Endereço de Entrega:
                </Typography>
                <Typography variant="body2">
                  {quote.shipping_address
                    ? `${quote.shipping_address.street}, ${quote.shipping_address.city}/${quote.shipping_address.state}, ${quote.shipping_address.zip_code}, ${quote.shipping_address.country}`
                    : 'Não informado'}
                </Typography>
              </Box>
              <Box display="flex" mb={1}>
                <Typography variant="body2" sx={{ width: '200px' }}>
                  Validade da Cotação:
                </Typography>
                <Typography variant="body2">
                  {quote.expiration_date
                    ? new Date(quote.expiration_date).toLocaleDateString('pt-BR')
                    : 'Não informado'}
                </Typography>
              </Box>
              <Box display="flex">
                <Typography variant="body2" sx={{ width: '200px' }}>
                  Criado por:
                </Typography>
                <Typography variant="body2">{quote.reseller?.name || 'Não informado'}</Typography>
              </Box>
            </Grid>
          </Grid>

          <Box display="flex" gap={2}>
            {/* Esconder o botão se a cotação estiver em Analise de Credito */}
            {quote.status !== 'Analise de Credito' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  converting ? <CircularProgress size={20} color="inherit" /> : <ShoppingCartIcon />
                }
                onClick={handleConvertToOrder}
                disabled={quote.status !== 'Aprovado' || converting}
                sx={{ color: 'white' }}
              >
                {converting ? 'Processando...' : 'Transformar em Pedido'}
              </Button>
            )}
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleGeneratePDF}>
              Gerar PDF
            </Button>
            <Button color="primary" startIcon={<ShareIcon />} onClick={handleShareLink}>
              Compartilhar Link
            </Button>
            <Button
              color="error"
              variant="outlined"
              startIcon={deletingQuote ? <CircularProgress size={20} /> : <DeleteIcon />}
              onClick={handleDeleteQuote}
              disabled={deletingQuote}
            >
              {deletingQuote ? 'Excluindo...' : 'Excluir Cotação'}
            </Button>
          </Box>
        </Paper>

        <Paper className={styles.section}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <img
                src="/icons/ultimos-pedidos.svg"
                alt="Quote icon"
                style={{
                  width: 24,
                  height: 24,
                  marginRight: '8px',
                }}
              />
              <Typography variant="h6">Produtos da Cotação</Typography>
            </Box>
            <Button
              variant="contained"
              sx={{ color: '#FFFFFF', backgroundColor: '#80c343' }}
              startIcon={<AddIcon />}
              onClick={handleOpenAddProductDialog}
              // disabled={quote.status !== 'Draft'}
            >
              Adicionar Produto
            </Button>
          </Box>

          <DataTable columns={columns} data={itemsWithSubtotal} />

          <Box display="flex" justifyContent="flex-end" mt={2} p={2}>
            <Typography variant="body1" fontWeight="medium">
              Total: {formatCurrency(total)}
            </Typography>
          </Box>
        </Paper>

        <Paper className={styles.section}>
          <Box display="flex" alignItems="center" mb={2}>
            <img
              src="/icons/credit-card.svg"
              alt="Quote icon"
              style={{
                width: 24,
                height: 24,
                marginRight: '8px',
              }}
            />
            <Typography variant="h6">Condições Comerciais</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: '#edf7ed',
                p: 2,
                borderRadius: 1,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 20, color: '#1E4620' }} />
              <Typography variant="body2">
                Método de pagamento:{' '}
                {quote.payment_method
                  ? translatePaymentMethod(quote.payment_method)
                  : 'Não informado'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: '#edf7ed',
                p: 2,
                borderRadius: 1,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 20, color: '#1E4620' }} />
              <Typography variant="body2">
                Prazo de pagamento: {quote.payment_term || 'Não informado'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Contacts Modal */}
        <Dialog
          open={openContactsModal}
          onClose={handleCloseContactsModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              minHeight: '60vh',
              maxHeight: '80vh',
            },
          }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <ContactsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Contatos de {quote.customer?.company_name || 'Cliente'}
                </Typography>
              </Box>
              <IconButton
                aria-label="fechar"
                onClick={handleCloseContactsModal}
                sx={{
                  color: theme => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {quote.customer?.contacts && quote.customer.contacts.length > 0 ? (
              <Grid container spacing={2}>
                {quote.customer.contacts.map((contact, index) => (
                  <Grid item xs={12} sm={6} key={contact.id || index}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        border: contact.is_primary ? '2px solid #80c343' : '1px solid #e0e0e0',
                        position: 'relative',
                      }}
                    >
                      {contact.is_primary && (
                        <Chip
                          label="Principal"
                          size="small"
                          color="primary"
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: 8,
                            backgroundColor: '#80c343',
                          }}
                        />
                      )}
                      <Box display="flex" alignItems="center" mb={1}>
                        <PersonIcon sx={{ mr: 1, color: '#80c343' }} />
                        <Typography variant="h6" fontWeight="medium">
                          {contact.name}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mb={1}>
                        <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                        <Typography variant="body2" color="text.secondary">
                          {contact.email}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mb={1}>
                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                        <Typography variant="body2" color="text.secondary">
                          {contact.phone}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        {contact.role}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="300px"
                color="text.secondary"
              >
                <ContactsIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  Nenhum contato encontrado
                </Typography>
                <Typography variant="body2" textAlign="center">
                  Este cliente ainda não possui contatos cadastrados.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseContactsModal} color="primary">
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Product Dialog */}
        <Dialog
          open={openAddProductDialog}
          onClose={handleCloseAddProductDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              minHeight: '80vh',
              maxHeight: '80vh',
            },
          }}
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Adicionar Produtos à Cotação</Typography>
              <TextField
                size="small"
                placeholder="Buscar produtos..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {loadingProducts ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {availableProducts.map(product => (
                  <Grid item xs={12} sm={6} md={4} key={product.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ position: 'relative', paddingTop: '100%' }}>
                        <CardMedia
                          component="img"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            padding: '8px',
                          }}
                          image={product.images?.[0]?.image_url || '/placeholder-product.png'}
                          alt={product.name}
                        />
                      </Box>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" noWrap>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          SKU: {product.sku}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(product.price)}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleAddProduct(product)}
                          fullWidth
                          disabled={addingProductId === product.id}
                        >
                          {addingProductId === product.id ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            'Adicionar'
                          )}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddProductDialog}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmação para análise de crédito */}
        <CreditAnalysisConfirmDialog
          open={creditDialogOpen}
          onClose={handleCreditDialogClose}
          onConfirm={handleCreditAnalysisConfirm}
          loading={converting}
        />
      </div>
    </>
  );
};

export default QuoteDetailsPage;
